import { PLATFORM_SECTIONS, type PlatformSection } from "@/shared/platform-activity/platformActivity";
import { HttpApiService } from "@/shared/services/httpApi.service";

import type {
  CardsCardholder,
  CardsDay,
  CardsDeclineReason,
  CardsMcc,
  CardsMerchant,
  CardsMetrics,
  CardsMetricsChange,
  CardsOrganization,
  CardsPeriodMetrics,
  Experiment,
  ExperimentOverride,
  ExperimentResults,
  ExperimentUser,
  ExperimentVariant,
  ExperimentStatus,
  OtcDay,
  OtcDayValues,
  OtcMetrics,
  OtcMetricsChange,
  OtcMetricsPeriod,
  OtcPeriodMetrics,
  OtcSideMetrics,
  TpvCorridor,
  TpvCorridorValues,
  TpvCustody,
  TpvCustodyCurrency,
  TpvDay,
  TpvFlowMetrics,
  TpvFxPair,
  TpvInFlightValues,
  TpvMetrics,
  TpvMetricsChange,
  TpvPeriodMetrics,
  TpvOrganization,
  UsersDuration,
  UsersFunnelStep,
  UsersMetrics,
  UsersMetricsChange,
  UsersPeriodMetrics,
  UsersRecent,
  FirstProduct,
  OnboardingStage,
  OnboardingStep,
  ActivityProduct,
  CardsBalanceHolder,
  CardsMonth,
  CardsSpendShareRow,
  CardsSpendShares,
  OrganizationInactive,
  OrganizationMover,
  OrganizationProducts,
  OtcMonth,
  OtcMonthValues,
  PlatformAudience,
  PlatformDay,
  PlatformHeatCell,
  PlatformMetrics,
  PlatformMetricsChange,
  PlatformPageUse,
  PlatformPeriodMetrics,
  PlatformSectionUse,
  ProductUsage,
  UsersRegistrationDay,
} from "../types/metrics.types";
import { ACTIVITY_PRODUCTS, ONBOARDING_STEPS, PLATFORM_DEVICES } from "../types/metrics.types";

const EMPTY_PERIOD: OtcPeriodMetrics = {
  transactions: 0,
  volume: "0",
  asset_volume: "0",
  average_ticket: null,
  average_asset_ticket: null,
  average_price: null,
  users: 0,
  new_users: 0,
  organizations: 0,
  transactions_per_user: null,
  duration: null,
  not_completed: { count: 0, rate: null, by_reason: {} },
  conversion: null,
};

const EMPTY_CHANGE: OtcMetricsChange = {
  transactions: null,
  volume: null,
  asset_volume: null,
  average_ticket: null,
  average_asset_ticket: null,
  average_price: null,
  users: null,
  new_users: null,
  transactions_per_user: null,
  duration_p50: null,
  not_completed_rate: null,
  conversion_rate: null,
};

function readPeriod(source: unknown): OtcPeriodMetrics {
  const period = (source ?? {}) as Partial<OtcPeriodMetrics>;
  return {
    ...EMPTY_PERIOD,
    ...period,
    not_completed: { ...EMPTY_PERIOD.not_completed, ...period.not_completed },
    duration: period.duration ?? null,
    conversion: period.conversion ?? null,
  };
}

function readSide(source: unknown): OtcSideMetrics {
  const side = (source ?? {}) as Partial<OtcSideMetrics>;
  return {
    current: readPeriod(side.current),
    previous: readPeriod(side.previous),
    change: { ...EMPTY_CHANGE, ...side.change },
    top_users: Array.isArray(side.top_users) ? side.top_users : [],
    quoters: Array.isArray(side.quoters) ? side.quoters : [],
  };
}

function readDayValues(source: Partial<OtcDayValues> | undefined): OtcDayValues {
  return {
    transactions: source?.transactions ?? 0,
    volume: source?.volume ?? "0",
    asset_volume: source?.asset_volume ?? "0",
    average_price: source?.average_price ?? null,
  };
}

function readDays(source: unknown): OtcDay[] {
  if (!Array.isArray(source)) return [];
  return source
    .filter((day): day is OtcDay => typeof (day as OtcDay | null)?.date === "string")
    .map((day) => ({
      date: day.date,
      buy: readDayValues(day.buy),
      sell: readDayValues(day.sell),
      card_top_up: readDayValues(day.card_top_up),
    }));
}

function readOtcMonthValues(source: unknown): OtcMonthValues {
  const row = (source ?? {}) as Record<string, unknown>;
  return {
    transactions: typeof row.transactions === "number" ? row.transactions : 0,
    volume: typeof row.volume === "string" ? row.volume : "0",
    asset_volume: typeof row.asset_volume === "string" ? row.asset_volume : "0",
  };
}

function readOtcMonth(row: Record<string, unknown>): OtcMonth | null {
  if (typeof row.month !== "string") return null;
  return {
    month: row.month,
    buy: readOtcMonthValues(row.buy),
    sell: readOtcMonthValues(row.sell),
    card_top_up: readOtcMonthValues(row.card_top_up),
  };
}

export function normalizeOtcMetrics(payload: unknown): OtcMetrics {
  const source = (payload ?? {}) as Partial<OtcMetrics>;
  return {
    days: typeof source.days === "number" ? source.days : 7,
    current: source.current ?? { from: "", to: "" },
    previous: source.previous ?? { from: "", to: "" },
    currency: source.currency ?? "COP",
    asset: source.asset ?? "USDT",
    organization: readOrganizationRef(source.organization),
    buy: readSide(source.buy),
    sell: readSide(source.sell),
    card_top_up: readSide(source.card_top_up),
    total: readSide(source.total),
    daily: {
      current: readDays(source.daily?.current),
      previous: readDays(source.daily?.previous),
    },
    monthly: arrayOf(source.monthly, readOtcMonth),
  };
}

const EMPTY_TPV_PERIOD: TpvPeriodMetrics = {
  transactions: 0,
  volume_usd: "0",
  average_ticket_usd: null,
  revenue: { total_usd: "0", service_usd: "0", conversion_usd: "0", fx_execution_usd: "0" },
  take_rate: null,
  organizations: 0,
  new_organizations: 0,
  not_completed: { count: 0, rate: null, by_reason: {} },
  expired: 0,
  unconverted_transactions: 0,
};

const EMPTY_TPV_CHANGE: TpvMetricsChange = {
  transactions: null,
  volume_usd: null,
  average_ticket_usd: null,
  revenue_usd: null,
  take_rate: null,
  organizations: null,
  new_organizations: null,
  not_completed_rate: null,
};

const EMPTY_CORRIDOR_VALUES: TpvCorridorValues = {
  transactions: 0,
  volume: "0",
  volume_usd: "0",
  revenue_usd: "0",
};

function arrayOf<T>(source: unknown, read: (row: Record<string, unknown>) => T | null): T[] {
  if (!Array.isArray(source)) return [];
  return source.flatMap((row) => {
    const value = read((row ?? {}) as Record<string, unknown>);
    return value === null ? [] : [value];
  });
}

function readTpvPeriod(source: unknown): TpvPeriodMetrics {
  const period = (source ?? {}) as Partial<TpvPeriodMetrics>;
  return {
    ...EMPTY_TPV_PERIOD,
    ...period,
    revenue: { ...EMPTY_TPV_PERIOD.revenue, ...period.revenue },
    not_completed: { ...EMPTY_TPV_PERIOD.not_completed, ...period.not_completed },
  };
}

function readTpvFlow(source: unknown): TpvFlowMetrics {
  const flow = (source ?? {}) as Partial<TpvFlowMetrics>;
  return {
    current: readTpvPeriod(flow.current),
    previous: readTpvPeriod(flow.previous),
    change: { ...EMPTY_TPV_CHANGE, ...flow.change },
  };
}

function readInFlight(source: unknown): TpvInFlightValues {
  const values = (source ?? {}) as Partial<TpvInFlightValues>;
  return { transactions: values.transactions ?? 0, volume_usd: values.volume_usd ?? "0" };
}

function readCorridorValues(source: unknown): TpvCorridorValues {
  return { ...EMPTY_CORRIDOR_VALUES, ...((source ?? {}) as Partial<TpvCorridorValues>) };
}

function readCorridor(row: Record<string, unknown>): TpvCorridor | null {
  const product = textOrNull(row.product) as TpvCorridor["product"] | null;
  if (!product) return null;
  return {
    direction: row.direction === "in" ? "in" : "out",
    product,
    rail: textOrNull(row.rail) ?? "",
    country: textOrNull(row.country) ?? "",
    currency: textOrNull(row.currency) ?? "",
    source_currency: textOrNull(row.source_currency) ?? textOrNull(row.currency) ?? "",
    current: readCorridorValues(row.current),
    previous: readCorridorValues(row.previous),
    share: typeof row.share === "number" ? row.share : null,
    change: typeof row.change === "number" ? row.change : null,
  };
}

function readFxPair(row: Record<string, unknown>): TpvFxPair | null {
  const origin = textOrNull(row.origin_currency);
  const destination = textOrNull(row.destination_currency);
  if (!origin || !destination) return null;
  return {
    origin_currency: origin,
    destination_currency: destination,
    current: readCorridorValues(row.current),
    previous: readCorridorValues(row.previous),
    change: typeof row.change === "number" ? row.change : null,
  };
}

function readOrganization(row: Record<string, unknown>): TpvOrganization | null {
  const id = textOrNull(row.organization_id);
  if (!id) return null;
  const number = (value: unknown) => (typeof value === "number" ? value : 0);
  return {
    organization_id: id,
    name: textOrNull(row.name),
    payins_usd: textOrNull(row.payins_usd) ?? "0",
    payouts_usd: textOrNull(row.payouts_usd) ?? "0",
    tpv_usd: textOrNull(row.tpv_usd) ?? "0",
    fx_usd: textOrNull(row.fx_usd) ?? "0",
    transactions_in: number(row.transactions_in),
    transactions_out: number(row.transactions_out),
    transactions: number(row.transactions),
    revenue_usd: textOrNull(row.revenue_usd) ?? "0",
    share: typeof row.share === "number" ? row.share : null,
    previous_tpv_usd: textOrNull(row.previous_tpv_usd) ?? "0",
    change: typeof row.change === "number" ? row.change : null,
  };
}

function readOrganizationRef(source: unknown): TpvMetrics["organization"] {
  const row = (source ?? {}) as Record<string, unknown>;
  const id = textOrNull(row.id);
  return id ? { id, name: textOrNull(row.name) } : null;
}

function readCustodyCurrency(row: Record<string, unknown>): TpvCustodyCurrency | null {
  const currency = textOrNull(row.currency);
  if (!currency) return null;
  const net = (row.net_change ?? {}) as Partial<TpvCustodyCurrency["net_change"]>;
  return {
    currency,
    wallets: typeof row.wallets === "number" ? row.wallets : 0,
    available: textOrNull(row.available) ?? "0",
    pending: textOrNull(row.pending) ?? "0",
    blocked: textOrNull(row.blocked) ?? "0",
    available_usd: textOrNull(row.available_usd),
    pending_usd: textOrNull(row.pending_usd),
    blocked_usd: textOrNull(row.blocked_usd),
    x_per_usd: textOrNull(row.x_per_usd),
    net_change: { current: net.current ?? "0", previous: net.previous ?? "0" },
  };
}

function readCustody(source: unknown): TpvCustody {
  const custody = (source ?? {}) as Partial<TpvCustody>;
  return {
    as_of: custody.as_of ?? "",
    wallets: custody.wallets ?? 0,
    new_wallets: { current: 0, previous: 0, ...custody.new_wallets },
    active_wallets: { current: 0, previous: 0, ...custody.active_wallets },
    available_usd: custody.available_usd ?? "0",
    pending_usd: custody.pending_usd ?? "0",
    blocked_usd: custody.blocked_usd ?? "0",
    by_currency: arrayOf(custody.by_currency, readCustodyCurrency),
  };
}

function readTpvDay(row: Record<string, unknown>): TpvDay | null {
  const date = textOrNull(row.date);
  if (!date) return null;
  return {
    date,
    payins_usd: textOrNull(row.payins_usd) ?? "0",
    payouts_usd: textOrNull(row.payouts_usd) ?? "0",
    fx_usd: textOrNull(row.fx_usd) ?? "0",
    transactions_in: typeof row.transactions_in === "number" ? row.transactions_in : 0,
    transactions_out: typeof row.transactions_out === "number" ? row.transactions_out : 0,
    revenue_usd: textOrNull(row.revenue_usd) ?? "0",
  };
}

export function normalizeTpvMetrics(payload: unknown): TpvMetrics {
  const source = (payload ?? {}) as Partial<TpvMetrics>;
  const inFlight = (source.in_flight ?? {}) as Partial<TpvMetrics["in_flight"]>;
  const rates = (source.rates ?? {}) as Partial<TpvMetrics["rates"]>;
  return {
    days: typeof source.days === "number" ? source.days : 30,
    current: source.current ?? { from: "", to: "" },
    previous: source.previous ?? { from: "", to: "" },
    currency: source.currency ?? "USD",
    organization: readOrganizationRef(source.organization),
    total: readTpvFlow(source.total),
    payins: readTpvFlow(source.payins),
    payouts: readTpvFlow(source.payouts),
    fx: readTpvFlow(source.fx),
    top_ups: readTpvFlow(source.top_ups),
    in_flight: { payins: readInFlight(inFlight.payins), payouts: readInFlight(inFlight.payouts) },
    corridors: arrayOf(source.corridors, readCorridor),
    fx_pairs: arrayOf(source.fx_pairs, readFxPair),
    organizations: arrayOf(source.organizations, readOrganization),
    custody: readCustody(source.custody),
    daily: {
      current: arrayOf(source.daily?.current, readTpvDay),
      previous: arrayOf(source.daily?.previous, readTpvDay),
    },
    rates: {
      filled_share: typeof rates.filled_share === "number" ? rates.filled_share : null,
      missing_currencies: Array.isArray(rates.missing_currencies)
        ? rates.missing_currencies.filter((code): code is string => typeof code === "string")
        : [],
      latest_rate_at: typeof rates.latest_rate_at === "string" ? rates.latest_rate_at : null,
    },
  };
}

function readExperiment(source: unknown): Experiment | null {
  const row = (source ?? {}) as Partial<Experiment>;
  if (typeof row.key !== "string" || typeof row.status !== "string") return null;
  return {
    key: row.key,
    description: row.description ?? "",
    status: row.status,
    variants: Array.isArray(row.variants) ? row.variants : [],
    control_variant: row.control_variant ?? "control",
    rollout_variant: row.rollout_variant ?? "treatment",
    started_at: row.started_at ?? "",
    ended_at: row.ended_at ?? null,
    users_by_variant: Array.isArray(row.users_by_variant) ? row.users_by_variant : [],
  };
}

export function normalizeExperiments(payload: unknown): Experiment[] {
  const rows = (payload as { experiments?: unknown } | null)?.experiments;
  if (!Array.isArray(rows)) return [];
  return rows.map(readExperiment).filter((row): row is Experiment => row !== null);
}

function textOrNull(value: unknown): string | null {
  return typeof value === "string" && value !== "" ? value : null;
}

function readOverride(source: unknown): ExperimentOverride | null {
  const row = (source ?? {}) as Record<string, unknown>;
  const userId = textOrNull(row.user_id);
  const variant = textOrNull(row.variant);
  if (!userId || !variant) return null;
  const forcedAt = textOrNull(row.forced_at);
  return {
    user_id: userId,
    email: textOrNull(row.email),
    name: textOrNull(row.name),
    organization_name: textOrNull(row.organization_name),
    variant,
    forced_at: forcedAt && !Number.isNaN(Date.parse(forcedAt)) ? forcedAt : null,
  };
}

export function normalizeExperimentResults(payload: unknown): ExperimentResults | null {
  const source = (payload ?? {}) as Partial<ExperimentResults>;
  const experiment = readExperiment(source.experiment);
  if (!experiment) return null;
  return {
    experiment,
    overrides: Array.isArray(source.overrides)
      ? source.overrides.map(readOverride).filter((row): row is ExperimentOverride => row !== null)
      : [],
    metric: typeof source.metric === "string" ? source.metric : null,
    value_currency: typeof source.value_currency === "string" ? source.value_currency : null,
    groups: Array.isArray(source.groups) ? source.groups : [],
    comparisons: Array.isArray(source.comparisons) ? source.comparisons : [],
    timeline: Array.isArray(source.timeline) ? source.timeline : [],
  };
}

export function normalizeExperimentUsers(payload: unknown): ExperimentUser[] {
  const rows = (payload as { users?: unknown } | null)?.users;
  if (!Array.isArray(rows)) return [];
  return rows.flatMap((source) => {
    const row = (source ?? {}) as Record<string, unknown>;
    const userId = textOrNull(row.user_id);
    const email = textOrNull(row.email);
    const variant = textOrNull(row.variant);
    if (!userId || !email || !variant) return [];
    return [
      {
        user_id: userId,
        email,
        name: textOrNull(row.name),
        organization_name: textOrNull(row.organization_name),
        variant,
        source: textOrNull(row.source) ?? "not_assigned",
        natural_variant: textOrNull(row.natural_variant) ?? variant,
        assigned_at: textOrNull(row.assigned_at),
      },
    ];
  });
}

const numberOr = (value: unknown, fallback = 0): number =>
  typeof value === "number" && Number.isFinite(value) ? value : fallback;
const rateOrNull = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;
const moneyOr = (value: unknown): string => (typeof value === "string" && value !== "" ? value : "0");

const EMPTY_CARDS_PERIOD: CardsPeriodMetrics = {
  spend_usd: "0",
  purchases: 0,
  average_ticket_usd: "0",
  declined: 0,
  approval_rate: null,
  active_cards: 0,
  active_cardholders: 0,
  new_cardholders: 0,
  issued_cards: 0,
  loads: { total_usd: "0", wallet_usd: "0", crypto_usd: "0", pesos_usd: "0", count: 0 },
};

const EMPTY_CARDS_CHANGE: CardsMetricsChange = {
  spend: null,
  purchases: null,
  average_ticket: null,
  approval_rate: null,
  active_cards: null,
  active_cardholders: null,
  loads: null,
  issued_cards: null,
};

function readCardsPeriod(source: unknown): CardsPeriodMetrics {
  const period = (source ?? {}) as Partial<CardsPeriodMetrics>;
  return {
    ...EMPTY_CARDS_PERIOD,
    ...period,
    approval_rate: rateOrNull(period.approval_rate),
    loads: { ...EMPTY_CARDS_PERIOD.loads, ...period.loads },
  };
}

function readCardsDay(row: Record<string, unknown>): CardsDay | null {
  if (typeof row.day !== "string") return null;
  return {
    day: row.day,
    approved_usd: moneyOr(row.approved_usd),
    approved: numberOr(row.approved),
    declined_usd: moneyOr(row.declined_usd),
    declined: numberOr(row.declined),
  };
}

function readCardsMerchant(row: Record<string, unknown>): CardsMerchant | null {
  if (typeof row.name !== "string") return null;
  return {
    name: row.name,
    mcc: textOrNull(row.mcc),
    spend_usd: moneyOr(row.spend_usd),
    purchases: numberOr(row.purchases),
    share: rateOrNull(row.share),
    cardholders: numberOr(row.cardholders),
  };
}

function readCardsMcc(row: Record<string, unknown>): CardsMcc | null {
  return { mcc: textOrNull(row.mcc), spend_usd: moneyOr(row.spend_usd), purchases: numberOr(row.purchases) };
}

function readCardsCardholder(row: Record<string, unknown>): CardsCardholder | null {
  return {
    identity_id: textOrNull(row.identity_id),
    name: textOrNull(row.name) ?? "—",
    organization_id: textOrNull(row.organization_id),
    organization_name: textOrNull(row.organization_name),
    cards: numberOr(row.cards),
    spend_usd: moneyOr(row.spend_usd),
    purchases: numberOr(row.purchases),
    average_ticket_usd: moneyOr(row.average_ticket_usd),
    declined: numberOr(row.declined),
    last_purchase_at: textOrNull(row.last_purchase_at),
  };
}

function readCardsOrganization(row: Record<string, unknown>): CardsOrganization | null {
  if (typeof row.organization_id !== "string") return null;
  return {
    organization_id: row.organization_id,
    name: textOrNull(row.name),
    active_cards: numberOr(row.active_cards),
    spend_usd: moneyOr(row.spend_usd),
    previous_spend_usd: moneyOr(row.previous_spend_usd),
    change: rateOrNull(row.change),
    approval_rate: rateOrNull(row.approval_rate),
  };
}

function readDeclineReason(row: Record<string, unknown>): CardsDeclineReason | null {
  if (typeof row.category !== "string") return null;
  return { category: row.category, count: numberOr(row.count), share: rateOrNull(row.share) };
}

function readCardsMonth(row: Record<string, unknown>): CardsMonth | null {
  if (typeof row.month !== "string") return null;
  return {
    month: row.month,
    spend_usd: moneyOr(row.spend_usd),
    purchases: numberOr(row.purchases),
    active_cards: numberOr(row.active_cards),
  };
}

function readBalanceHolder(row: Record<string, unknown>): CardsBalanceHolder | null {
  if (typeof row.organization_id !== "string") return null;
  return {
    organization_id: row.organization_id,
    name: textOrNull(row.name),
    wallet_usd: moneyOr(row.wallet_usd),
    cards_usd: moneyOr(row.cards_usd),
    total_usd: moneyOr(row.total_usd),
    share: rateOrNull(row.share),
    cards_with_balance: numberOr(row.cards_with_balance),
  };
}

function readSpendShares(source: unknown): CardsSpendShares | null {
  if (!source || typeof source !== "object") return null;
  const row = source as Record<string, unknown>;
  const months = Array.isArray(row.months)
    ? row.months.filter((m): m is string => typeof m === "string")
    : [];
  if (months.length === 0) return null;
  const strings = (value: unknown) => (Array.isArray(value) ? value.map(moneyOr) : months.map(() => "0"));
  return {
    months,
    totals_usd: strings(row.totals_usd),
    organizations: arrayOf(
      row.organizations,
      (line): CardsSpendShareRow => ({
        organization_id: textOrNull(line.organization_id),
        name: textOrNull(line.name),
        total_usd: moneyOr(line.total_usd),
        spend_usd: strings(line.spend_usd),
        shares: Array.isArray(line.shares) ? line.shares.map(rateOrNull) : months.map(() => null),
      }),
    ),
    others_count: numberOr(row.others_count),
  };
}

export function normalizeCardsMetrics(payload: unknown): CardsMetrics {
  const source = (payload ?? {}) as Partial<CardsMetrics>;
  const figures = (source.figures ?? {}) as Partial<CardsMetrics["figures"]>;
  const holdings = (source.holdings ?? {}) as Partial<CardsMetrics["holdings"]>;
  return {
    days: numberOr(source.days, 30),
    current: source.current ?? { from: "", to: "" },
    previous: source.previous ?? { from: "", to: "" },
    currency: source.currency ?? "USD",
    organization: readOrganizationRef(source.organization),
    figures: {
      current: readCardsPeriod(figures.current),
      previous: readCardsPeriod(figures.previous),
      change: { ...EMPTY_CARDS_CHANGE, ...figures.change },
    },
    holdings: {
      balance_usd: moneyOr(holdings.balance_usd),
      cards_with_balance: numberOr(holdings.cards_with_balance),
    },
    daily: arrayOf(source.daily, readCardsDay),
    merchants: arrayOf(source.merchants, readCardsMerchant),
    mcc: arrayOf(source.mcc, readCardsMcc),
    cardholders: arrayOf(source.cardholders, readCardsCardholder),
    organizations: arrayOf(source.organizations, readCardsOrganization),
    decline_reasons: arrayOf(source.decline_reasons, readDeclineReason),
    monthly: arrayOf(source.monthly, readCardsMonth),
    balance_holders: Array.isArray(source.balance_holders)
      ? arrayOf(source.balance_holders, readBalanceHolder)
      : null,
    spend_shares: readSpendShares(source.spend_shares),
  };
}

const EMPTY_USERS_PERIOD: UsersPeriodMetrics = {
  registered: 0,
  email_verified: 0,
  kyc_started: 0,
  kyc_approved: 0,
  kyc_rejected: 0,
  first_transaction: 0,
  active_organizations: 0,
};

const EMPTY_USERS_CHANGE: UsersMetricsChange = {
  registered: null,
  kyc_started: null,
  kyc_approved: null,
  first_transaction: null,
  active_organizations: null,
};

const isStep = (value: unknown): value is OnboardingStep =>
  typeof value === "string" && (ONBOARDING_STEPS as readonly string[]).includes(value);

function readFunnelStep(row: Record<string, unknown>): UsersFunnelStep | null {
  if (!isStep(row.step)) return null;
  return {
    step: row.step,
    count: numberOr(row.count),
    of_registered: rateOrNull(row.of_registered),
    of_previous_step: rateOrNull(row.of_previous_step),
  };
}

function readDuration(row: Record<string, unknown>): UsersDuration | null {
  if (!isStep(row.from) || !isStep(row.to)) return null;
  return {
    from: row.from,
    to: row.to,
    p50_seconds: rateOrNull(row.p50_seconds),
    p90_seconds: rateOrNull(row.p90_seconds),
    samples: numberOr(row.samples),
  };
}

function readRecent(row: Record<string, unknown>): UsersRecent | null {
  if (typeof row.organization_id !== "string" || typeof row.registered_at !== "string") return null;
  return {
    organization_id: row.organization_id,
    name: textOrNull(row.name),
    email: textOrNull(row.email),
    registered_at: row.registered_at,
    stage: (typeof row.stage === "string" ? row.stage : "registered") as OnboardingStage,
    stage_since: textOrNull(row.stage_since),
    last_login_at: textOrNull(row.last_login_at),
  };
}

const productsOf = (value: unknown): ActivityProduct[] =>
  Array.isArray(value) ? ACTIVITY_PRODUCTS.filter((product) => value.includes(product)) : [];

function readMover(row: Record<string, unknown>): OrganizationMover | null {
  if (typeof row.organization_id !== "string") return null;
  return {
    organization_id: row.organization_id,
    name: textOrNull(row.name),
    current_usd: moneyOr(row.current_usd),
    previous_usd: moneyOr(row.previous_usd),
    difference_usd: moneyOr(row.difference_usd),
    change: rateOrNull(row.change),
    current_operations: numberOr(row.current_operations),
    previous_operations: numberOr(row.previous_operations),
    last_operation_at: textOrNull(row.last_operation_at),
    products: productsOf(row.products),
  };
}

function readInactive(row: Record<string, unknown>): OrganizationInactive | null {
  if (typeof row.organization_id !== "string" || typeof row.last_operation_at !== "string") return null;
  return {
    organization_id: row.organization_id,
    name: textOrNull(row.name),
    last_operation_at: row.last_operation_at,
    days_inactive: numberOr(row.days_inactive),
    lookback_usd: moneyOr(row.lookback_usd),
    lookback_operations: numberOr(row.lookback_operations),
    products: productsOf(row.products),
  };
}

function readOrganizationProducts(row: Record<string, unknown>): OrganizationProducts | null {
  if (typeof row.organization_id !== "string") return null;
  return {
    organization_id: row.organization_id,
    name: textOrNull(row.name),
    lookback_usd: moneyOr(row.lookback_usd),
    used: productsOf(row.used),
    recent: productsOf(row.recent),
    never: productsOf(row.never),
  };
}

function readRegistrationDay(row: Record<string, unknown>): UsersRegistrationDay | null {
  if (typeof row.date !== "string") return null;
  return {
    date: row.date,
    previous_date: textOrNull(row.previous_date) ?? "",
    current: numberOr(row.current),
    previous: numberOr(row.previous),
  };
}

export function normalizeUsersMetrics(payload: unknown): UsersMetrics {
  const source = (payload ?? {}) as Partial<UsersMetrics>;
  const figures = (source.figures ?? {}) as Partial<UsersMetrics["figures"]>;
  const attention = (source.attention ?? {}) as Partial<UsersMetrics["attention"]>;
  const movers = (source.movers ?? {}) as Partial<UsersMetrics["movers"]>;
  const inactive = (source.inactive ?? {}) as Partial<UsersMetrics["inactive"]>;
  const products = (source.products ?? {}) as Partial<UsersMetrics["products"]>;
  return {
    days: numberOr(source.days, 30),
    current: source.current ?? { from: "", to: "" },
    previous: source.previous ?? { from: "", to: "" },
    figures: {
      current: { ...EMPTY_USERS_PERIOD, ...figures.current },
      previous: { ...EMPTY_USERS_PERIOD, ...figures.previous },
      change: { ...EMPTY_USERS_CHANGE, ...figures.change },
    },
    funnel: arrayOf(source.funnel, readFunnelStep),
    durations: arrayOf(source.durations, readDuration),
    first_products: arrayOf(source.first_products, (row) =>
      typeof row.product === "string"
        ? { product: row.product as FirstProduct, count: numberOr(row.count) }
        : null,
    ),
    recent: arrayOf(source.recent, readRecent),
    attention: {
      verified_without_transaction: numberOr(attention.verified_without_transaction),
      verified_without_transaction_days: numberOr(attention.verified_without_transaction_days, 14),
      kyc_in_review_stale: numberOr(attention.kyc_in_review_stale),
      kyc_in_review_days: numberOr(attention.kyc_in_review_days, 3),
    },
    registrations_daily: arrayOf(source.registrations_daily, readRegistrationDay),
    movers: {
      drop_threshold: numberOr(movers.drop_threshold, 0.3),
      growth_floor_usd: moneyOr(movers.growth_floor_usd),
      falling_count: numberOr(movers.falling_count),
      growing_count: numberOr(movers.growing_count),
      falling: arrayOf(movers.falling, readMover),
      growing: arrayOf(movers.growing, readMover),
    },
    inactive: {
      days: numberOr(inactive.days, 14),
      lookback_days: numberOr(inactive.lookback_days, 120),
      count: numberOr(inactive.count),
      organizations: arrayOf(inactive.organizations, readInactive),
    },
    products: {
      lookback_days: numberOr(products.lookback_days, 120),
      organizations: numberOr(products.organizations),
      usage: arrayOf(products.usage, (row): ProductUsage | null =>
        (ACTIVITY_PRODUCTS as readonly unknown[]).includes(row.product)
          ? {
              product: row.product as ActivityProduct,
              used_ever: numberOr(row.used_ever),
              used_recently: numberOr(row.used_recently),
            }
          : null,
      ),
      by_organization: arrayOf(products.by_organization, readOrganizationProducts),
    },
  };
}

const EMPTY_PLATFORM_PERIOD: PlatformPeriodMetrics = {
  active_users: 0,
  organizations: 0,
  sessions: 0,
  visits: 0,
  active_ms: 0,
  sessions_per_user: null,
  session_p50_ms: null,
  session_avg_ms: null,
  logins: 0,
  login_users: 0,
  daily_active_users: null,
};

const EMPTY_PLATFORM_CHANGE: PlatformMetricsChange = {
  active_users: null,
  sessions: null,
  session_p50: null,
  logins: null,
  daily_active_users: null,
};

const sectionOf = (value: unknown): PlatformSection =>
  (PLATFORM_SECTIONS as readonly unknown[]).includes(value) ? (value as PlatformSection) : "other";

function readPlatformPeriod(source: unknown): PlatformPeriodMetrics {
  const row = (source ?? {}) as Record<string, unknown>;
  return {
    active_users: numberOr(row.active_users),
    organizations: numberOr(row.organizations),
    sessions: numberOr(row.sessions),
    visits: numberOr(row.visits),
    active_ms: numberOr(row.active_ms),
    sessions_per_user: rateOrNull(row.sessions_per_user),
    session_p50_ms: rateOrNull(row.session_p50_ms),
    session_avg_ms: rateOrNull(row.session_avg_ms),
    logins: numberOr(row.logins),
    login_users: numberOr(row.login_users),
    daily_active_users: rateOrNull(row.daily_active_users),
  };
}

export function normalizePlatformMetrics(payload: unknown): PlatformMetrics {
  const source = (payload ?? {}) as Record<string, unknown>;
  const figures = (source.figures ?? {}) as Record<string, unknown>;
  return {
    days: numberOr(source.days, 30),
    current: (source.current as PlatformMetrics["current"]) ?? { from: "", to: "" },
    previous: (source.previous as PlatformMetrics["previous"]) ?? { from: "", to: "" },
    audience: source.audience === "staff" ? "staff" : "clients",
    tracking_since: textOrNull(source.tracking_since),
    figures: {
      current: figures.current ? readPlatformPeriod(figures.current) : EMPTY_PLATFORM_PERIOD,
      previous: figures.previous ? readPlatformPeriod(figures.previous) : EMPTY_PLATFORM_PERIOD,
      change: { ...EMPTY_PLATFORM_CHANGE, ...(figures.change as Partial<PlatformMetricsChange>) },
    },
    weekly_active_users: numberOr(source.weekly_active_users),
    daily: arrayOf(source.daily, (row): PlatformDay | null =>
      typeof row.date === "string"
        ? {
            date: row.date,
            active_users: numberOr(row.active_users),
            sessions: numberOr(row.sessions),
            active_ms: numberOr(row.active_ms),
            logins: numberOr(row.logins),
          }
        : null,
    ),
    sections: arrayOf(
      source.sections,
      (row): PlatformSectionUse => ({
        section: sectionOf(row.section),
        visits: numberOr(row.visits),
        users: numberOr(row.users),
        active_ms: numberOr(row.active_ms),
        share: rateOrNull(row.share),
        previous_active_ms: numberOr(row.previous_active_ms),
        change: rateOrNull(row.change),
      }),
    ),
    pages: arrayOf(source.pages, (row): PlatformPageUse | null =>
      typeof row.page === "string"
        ? {
            page: row.page,
            section: sectionOf(row.section),
            visits: numberOr(row.visits),
            users: numberOr(row.users),
            active_ms: numberOr(row.active_ms),
            share: rateOrNull(row.share),
          }
        : null,
    ),
    heatmap: arrayOf(source.heatmap, (row): PlatformHeatCell | null =>
      typeof row.weekday === "number" && typeof row.hour === "number"
        ? {
            weekday: row.weekday,
            hour: row.hour,
            active_ms: numberOr(row.active_ms),
            visits: numberOr(row.visits),
            logins: numberOr(row.logins),
          }
        : null,
    ),
    devices: arrayOf(source.devices, (row) =>
      (PLATFORM_DEVICES as readonly unknown[]).includes(row.device)
        ? {
            device: row.device as PlatformMetrics["devices"][number]["device"],
            sessions: numberOr(row.sessions),
            users: numberOr(row.users),
          }
        : null,
    ),
    browsers: arrayOf(source.browsers, (row) =>
      typeof row.label === "string" ? { label: row.label, sessions: numberOr(row.sessions) } : null,
    ),
  };
}

// The backend keeps each answer for a minute; `refresh` (the page's refresh button) asks it to read again.
function metricsQuery(days: OtcMetricsPeriod, refresh: boolean, organizationId: string | null = null) {
  const query = new URLSearchParams({ days: String(days) });
  if (organizationId) query.set("organization_id", organizationId);
  if (refresh) query.set("refresh", "true");
  return query;
}

export const MetricsService = {
  async access(): Promise<boolean> {
    const { data } = await HttpApiService.get<{ allowed: boolean }>("/internal/metrics/access");
    return data.allowed === true;
  },

  async otc(
    days: OtcMetricsPeriod,
    organizationId: string | null = null,
    refresh = false,
  ): Promise<OtcMetrics> {
    const { data } = await HttpApiService.get<unknown>(
      `/internal/metrics/otc?${metricsQuery(days, refresh, organizationId)}`,
    );
    return normalizeOtcMetrics(data);
  },

  async tpv(
    days: OtcMetricsPeriod,
    organizationId: string | null = null,
    refresh = false,
  ): Promise<TpvMetrics> {
    const { data } = await HttpApiService.get<unknown>(
      `/internal/metrics/tpv?${metricsQuery(days, refresh, organizationId)}`,
    );
    return normalizeTpvMetrics(data);
  },

  async cards(
    days: OtcMetricsPeriod,
    organizationId: string | null = null,
    refresh = false,
  ): Promise<CardsMetrics> {
    const { data } = await HttpApiService.get<unknown>(
      `/internal/metrics/cards?${metricsQuery(days, refresh, organizationId)}`,
    );
    return normalizeCardsMetrics(data);
  },

  async users(days: OtcMetricsPeriod, refresh = false): Promise<UsersMetrics> {
    const { data } = await HttpApiService.get<unknown>(
      `/internal/metrics/users?${metricsQuery(days, refresh)}`,
    );
    return normalizeUsersMetrics(data);
  },

  async platform(
    days: OtcMetricsPeriod,
    audience: PlatformAudience,
    refresh = false,
  ): Promise<PlatformMetrics> {
    const query = metricsQuery(days, refresh);
    query.set("audience", audience);
    const { data } = await HttpApiService.get<unknown>(`/internal/metrics/platform?${query}`);
    return normalizePlatformMetrics(data);
  },

  async experiments(): Promise<Experiment[]> {
    const { data } = await HttpApiService.get<unknown>("/internal/experiments");
    return normalizeExperiments(data);
  },

  async experimentResults(key: string): Promise<ExperimentResults | null> {
    const { data } = await HttpApiService.get<unknown>(
      `/internal/experiments/${encodeURIComponent(key)}/results`,
    );
    return normalizeExperimentResults(data);
  },

  async lookupUser(key: string, email: string): Promise<ExperimentUser> {
    const { data } = await HttpApiService.get<ExperimentUser>(
      `/internal/experiments/${encodeURIComponent(key)}/users?email=${encodeURIComponent(email)}`,
    );
    return data;
  },

  async searchUsers(key: string, query: string): Promise<ExperimentUser[]> {
    const { data } = await HttpApiService.get<unknown>(
      `/internal/experiments/${encodeURIComponent(key)}/users/search?q=${encodeURIComponent(query)}`,
    );
    return normalizeExperimentUsers(data);
  },

  async forceVariant(key: string, email: string, variant: string): Promise<ExperimentOverride> {
    const { data } = await HttpApiService.put<ExperimentOverride>(
      `/internal/experiments/${encodeURIComponent(key)}/overrides`,
      { email, variant },
    );
    return data;
  },

  async releaseVariant(key: string, userId: string): Promise<void> {
    await HttpApiService.delete(
      `/internal/experiments/${encodeURIComponent(key)}/overrides/${encodeURIComponent(userId)}`,
    );
  },

  async updateWeights(key: string, variants: ExperimentVariant[]): Promise<Experiment | null> {
    const { data } = await HttpApiService.put<unknown>(
      `/internal/experiments/${encodeURIComponent(key)}/weights`,
      { variants },
    );
    return readExperiment(data);
  },

  async updateExperiment(key: string, status: ExperimentStatus): Promise<Experiment | null> {
    const { data } = await HttpApiService.patch<unknown>(`/internal/experiments/${encodeURIComponent(key)}`, {
      status,
    });
    return readExperiment(data);
  },
};
